import { BRANCHES, LAND, LAND_LEVELS, SANCTUM, STRUCTURES, WATCHTOWER } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Structure } from '../types.ts';
import { beacons, healHero } from './combat.ts';

/*
 * The era IV buildings in a fight (docs/ERAS.md, "Eras IV y V"): the ranger's watchtower and the cleric's
 * sanctum. The trebuchet is a tower and fires with the others (towers.ts).
 */

/**
 * The watchtowers among the structures, with their centers, so the checks don't walk the whole ring wall. Like
 * the beacons in combat.ts: the list grows by push and is replaced when something is removed, so its identity
 * and length tell whether this is current (a watchtower that falls is skipped by `destroyed`).
 */
const WATCH = new WeakMap<Structure[], { n: number; list: { st: Structure; x: number; y: number }[] }>();
const watchtowers = (s: BattleState): { st: Structure; x: number; y: number }[] => {
  const m = WATCH.get(s.structures);
  if (m && m.n === s.structures.length) return m.list;
  const list = s.structures.filter((st) => st.type === 'watchtower').map((st) => ({ st, ...hexToWorld(st.q, st.r) }));
  WATCH.set(s.structures, { n: s.structures.length, list });
  return list;
};

/** A ranger standing in the fight: the watchtower sees farther and its towers reach farther. */
const rangerFights = (s: BattleState): boolean => s.heroes.some((h) => h.cls === 'ranger' && h.downTicks === 0);

/**
 * Whether a point lies under a standing watchtower's eye: the invisible there are revealed, and what goes under
 * the ground there is marked, so it can be seen and hit before it surfaces.
 *
 * The hook for buried nightmares: `targetable` (combat.ts) lets a burrowing sapper be hit where this is true, and
 * a nightmare that travels underground (the era IV shell serpent) should ask `spotted(s, e.x, e.y)` wherever it
 * decides it can't be hit or seen while buried. The board draws a pale mark over a buried one that is spotted.
 */
export const spotted = (s: BattleState, x: number, y: number): boolean => {
  const list = watchtowers(s);
  if (list.length === 0) return false;
  const r = rangerFights(s) ? WATCHTOWER.rangerReveal : WATCHTOWER.reveal;
  for (const w of list) if (!w.st.destroyed && withinRange(x, y, w.x, w.y, s.map.land ? mulBp(r, LAND_LEVELS.watchtower.revealBp[w.st.level - 1] ?? 10000) : r)) return true;
  return false;
};

/**
 * A bastion wave under fog (docs/PRESAGIOS.md §3.1): the tower's reach shrinks, unless a standing beacon reaches it
 * (the beacon cuts the fog). A tower Bruni took keeps its reach, or the fog would make him easier; a sealed one he
 * took still shoots the nightmares, so it shortens like the rest.
 */
const fogged = (s: BattleState, st: Structure, range: number): number => {
  const bp = s.rules?.towerRangeBp;
  // The belfry's toll is an aura, not a shot.
  if (!bp || turnedOnHeroes(s, st) || (st.mount?.type ?? st.type) === 'belfry') return range;
  const c = hexToWorld(st.q, st.r);
  for (const b of beacons(s)) if (!b.st.destroyed && b.st.id !== st.id && withinRange(c.x, c.y, b.x, b.y, STRUCTURES.beacon.range)) return range;
  return mulBp(range, bp);
};

/** A tower Bruni took that shoots the heroes: taken and not sealed (a sealed one shoots the nightmares still). */
export const turnedOnHeroes = (s: BattleState, st: Structure): boolean => (st.turnedUntil ?? 0) > s.tick && !(st.branch && BRANCHES[st.branch].sealed);

/**
 * A tower's reach with a standing watchtower near it: `range` grown by its share. `clear` leaves the fog out: Bruni
 * picks his towers by their clear-day reach, or the fog would have him take fewer.
 */
export const towerRange = (s: BattleState, st: Structure, range: number, clear = false): number => {
  // On terraced land a tower reaches farther the higher it stands (docs/CONSTRUCCION.md).
  const land = s.map.land;
  if (land) range += (land.level[cellId(s.map.radius, st.q, st.r)] ?? 0) * LAND.levelRange;
  if (!clear) range = fogged(s, st, range);
  const list = watchtowers(s);
  if (list.length === 0) return range;
  const c = hexToWorld(st.q, st.r);
  for (const w of list) {
    if (w.st.destroyed || !withinRange(c.x, c.y, w.x, w.y, STRUCTURES.watchtower.range)) continue;
    const bonus = rangerFights(s) ? WATCHTOWER.rangerRangeBp : WATCHTOWER.rangeBp;
    // On terraced land the watchtower's level grows its share (LAND_LEVELS).
    return mulBp(range, 10000 + (s.map.land ? mulBp(bonus, LAND_LEVELS.watchtower.rangeBp[w.st.level - 1] ?? 10000) : bonus));
  }
  return range;
};

/** The sanctum blesses the heroes standing by it: a little health every second, twice with a cleric in the fight. */
export const sanctumBless = (s: BattleState, st: Structure): void => {
  if (st.type !== 'sanctum' || st.destroyed || s.tick % 12 !== 0) return;
  const c = hexToWorld(st.q, st.r);
  const base = s.heroes.some((h) => h.cls === 'cleric' && h.downTicks === 0) ? SANCTUM.clericBlessBp : SANCTUM.blessBp;
  const bp = s.map.land ? mulBp(base, LAND_LEVELS.sanctum.blessBp[st.level - 1] ?? 10000) : base;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || h.hp >= h.stats.maxHp || !withinRange(h.x, h.y, c.x, c.y, STRUCTURES.sanctum.range)) continue;
    healHero(s, h, imax(1, mulBp(h.stats.maxHp, bp)), h.pid);
  }
};
